<template>
  <section
    v-if="restaurant?.mailing_list_available && restaurant?.slug"
    id="mailing-list"
    class="rms-mailing-list"
    data-testid="mailing-list-section"
    :class="templateClass"
    aria-labelledby="mailing-list-heading"
  >
    <!-- Template 2: two-column layout per reference code.html -->
    <div v-if="template === 'template-2'" class="rms-mailing-list__inner rms-mailing-list__inner--t2">
      <div class="rms-mailing-list__t2-grid">
        <div class="rms-mailing-list__t2-copy">
          <p class="font-mono text-oxidized-copper text-sm mb-4 tracking-tighter uppercase font-bold">// {{ $t('public.t2CommunicationsLink') }}</p>
          <h2 id="mailing-list-heading" class="rms-mailing-list__title rms-mailing-list__title--t2">{{ $t('public.t2NewsletterTitle') }}</h2>
          <p class="rms-mailing-list__description rms-mailing-list__description--t2">{{ $t('public.mailingListDescription') }}</p>
        </div>
        <div class="rms-mailing-list__t2-form-wrap">
          <div class="rms-mailing-list__t2-form-bg" aria-hidden="true" />
          <form
            novalidate
            class="rms-mailing-list__form rms-mailing-list__form--t2"
            :aria-busy="submitting"
            @submit.prevent="submit"
          >
            <p v-if="messageError" class="rms-mailing-list__error" role="alert">{{ messageError }}</p>
            <p v-if="messageSuccess" class="rms-mailing-list__success" role="status">{{ messageSuccess }}</p>
            <div class="rms-mailing-list__t2-row">
              <label for="mailing-list-email" class="rms-sr-only">{{ $t('public.mailingListEmailLabel') }}</label>
              <div class="rms-mailing-list__t2-input-wrap">
                <span class="material-symbols-outlined text-charcoal-blue/40 mr-4" aria-hidden="true">alternate_email</span>
                <input
                  id="mailing-list-email"
                  v-model="email"
                  type="email"
                  data-testid="mailing-list-email"
                  autocomplete="email"
                  class="rms-mailing-list__input rms-mailing-list__input--t2"
                  :placeholder="$t('public.mailingListEmailPlaceholder')"
                  :aria-invalid="!!fieldError"
                  :aria-describedby="fieldError ? 'mailing-list-email-error' : undefined"
                  :disabled="submitting"
                >
              </div>
            <div v-if="recaptchaEnabled && !recaptchaIsV3" class="w-full flex justify-center min-h-[78px] mt-2">
              <div ref="recaptchaContainerRef" aria-label="reCAPTCHA" />
            </div>
              <button
                type="submit"
                class="rms-mailing-list__submit rms-mailing-list__submit--t2"
                :class="{ 'rms-mailing-list__submit--busy': submitting }"
                :disabled="submitting"
                data-testid="mailing-list-subscribe-btn"
              >
                {{ submitting ? $t('app.loading') : $t('public.mailingListSubscribe') }}
                <span class="material-symbols-outlined" aria-hidden="true">arrow_forward</span>
              </button>
            </div>
            <p id="mailing-list-email-error" v-if="fieldError" class="rms-mailing-list__field-error">{{ fieldError }}</p>
          </form>
        </div>
      </div>
    </div>
    <!-- Template 3: dark theme "Join Our Inner Circle" style -->
    <div v-else-if="template === 'template-3'" class="py-16 sm:py-20 bg-t3-primary/5 border-y border-t3-primary/10">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 text-center">
        <h2 id="mailing-list-heading" class="text-xl sm:text-2xl font-bold mb-4 text-slate-100">{{ $t('public.t3JoinInnerCircle') }}</h2>
        <p class="text-slate-400 mb-8">{{ $t('public.t3JoinInnerCircleDesc') }}</p>
        <form
          novalidate
          class="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
          :aria-busy="submitting"
          @submit.prevent="submit"
        >
          <p v-if="messageError" class="w-full text-red-400 text-sm" role="alert">{{ messageError }}</p>
          <p v-if="messageSuccess" class="w-full text-green-400 text-sm" role="status">{{ messageSuccess }}</p>
          <label for="mailing-list-email-t3" class="rms-sr-only">{{ $t('public.mailingListEmailLabel') }}</label>
          <input
            id="mailing-list-email-t3"
            v-model="email"
            type="email"
            data-testid="mailing-list-email"
            autocomplete="email"
            class="flex-1 bg-t3-bg-dark border border-white/10 rounded-lg px-4 py-3 text-slate-100 focus:border-t3-primary outline-none min-h-[44px] placeholder:text-slate-500"
            :placeholder="$t('public.mailingListEmailPlaceholder')"
            :aria-invalid="!!fieldError"
            :aria-describedby="fieldError ? 'mailing-list-email-t3-error' : undefined"
            :disabled="submitting"
          >
          <div v-if="recaptchaEnabled && !recaptchaIsV3" class="w-full flex justify-center min-h-[78px]">
            <div ref="recaptchaContainerRef" aria-label="reCAPTCHA" />
          </div>
          <button
            type="submit"
            class="bg-t3-primary text-t3-bg-dark font-bold px-8 py-3 rounded-lg hover:scale-105 transition-transform min-h-[44px] disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-t3-primary"
            :disabled="submitting"
            data-testid="mailing-list-subscribe-btn"
          >
            {{ submitting ? $t('app.loading') : $t('public.t3Join') }}
          </button>
        </form>
        <p id="mailing-list-email-t3-error" v-if="fieldError" class="text-red-400 text-sm mt-2">{{ fieldError }}</p>
      </div>
    </div>
    <!-- Template 1: centered layout similar to Template 3, with T1 colors -->
    <div v-else-if="template === 'template-1'" class="py-16 sm:py-20 rms-mailing-list__t1-section">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 text-center">
        <h2 id="mailing-list-heading" class="text-xl sm:text-2xl font-bold mb-4 text-t1-neutral-dark">{{ $t('public.mailingListHeading') }}</h2>
        <p class="text-t1-neutral-muted mb-8">{{ $t('public.mailingListDescription') }}</p>
        <form
          novalidate
          class="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
          :aria-busy="submitting"
          @submit.prevent="submit"
        >
          <p v-if="messageError" class="w-full text-red-600 text-sm" role="alert">{{ messageError }}</p>
          <p v-if="messageSuccess" class="w-full text-green-600 text-sm" role="status">{{ messageSuccess }}</p>
          <label for="mailing-list-email-t1" class="rms-sr-only">{{ $t('public.mailingListEmailLabel') }}</label>
          <input
            id="mailing-list-email-t1"
            v-model="email"
            type="email"
            data-testid="mailing-list-email"
            autocomplete="email"
            class="rms-mailing-list__t1-input flex-1 min-h-[44px] rounded-lg px-4 py-3 border bg-white text-t1-neutral-dark placeholder:text-t1-neutral-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-0 disabled:opacity-60"
            :placeholder="$t('public.mailingListEmailPlaceholder')"
            :aria-invalid="!!fieldError"
            :aria-describedby="fieldError ? 'mailing-list-email-t1-error' : undefined"
            :disabled="submitting"
          >
          <div v-if="recaptchaEnabled && !recaptchaIsV3" class="w-full flex justify-center min-h-[78px]">
          <div ref="recaptchaContainerRef" aria-label="reCAPTCHA" />
        </div>
          <button
            type="submit"
            class="rms-mailing-list__t1-submit font-bold px-8 py-3 rounded-lg min-h-[44px] disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 transition-transform hover:scale-[1.02] disabled:hover:scale-100"
            :disabled="submitting"
            data-testid="mailing-list-subscribe-btn"
          >
            {{ submitting ? $t('app.loading') : $t('public.mailingListSubscribe') }}
          </button>
        </form>
        <p id="mailing-list-email-t1-error" v-if="fieldError" class="text-red-600 text-sm mt-2">{{ fieldError }}</p>
      </div>
    </div>
    <!-- Template 4: "Stay in the Loop" style from code.html -->
    <div v-else-if="template === 'template-4'" class="py-20 bg-[var(--rms-t4-primary,#ec5b13)]/10">
      <div class="max-w-7xl mx-auto px-6">
        <div class="max-w-2xl mx-auto text-center">
          <h2 id="mailing-list-heading" class="text-3xl font-bold mb-4 text-slate-900 dark:text-slate-100">{{ $t('public.mailingListHeading') }}</h2>
          <p class="text-slate-600 dark:text-slate-400 mb-8">{{ $t('public.mailingListDescription') }}</p>
          <form
            novalidate
            class="flex flex-col sm:flex-row gap-3"
            :aria-busy="submitting"
            @submit.prevent="submit"
          >
            <p v-if="messageError" class="w-full text-red-600 dark:text-red-400 text-sm" role="alert">{{ messageError }}</p>
            <p v-if="messageSuccess" class="w-full text-green-600 dark:text-green-400 text-sm" role="status">{{ messageSuccess }}</p>
            <label for="mailing-list-email-t4" class="rms-sr-only">{{ $t('public.mailingListEmailLabel') }}</label>
            <input
id="mailing-list-email-t4"
            v-model="email"
            type="email"
            data-testid="mailing-list-email"
            autocomplete="email"
            class="flex-grow px-6 py-4 rounded-xl border-none ring-1 ring-slate-200 dark:ring-slate-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-[var(--rms-t4-primary,#ec5b13)] outline-none min-h-[44px] placeholder:text-slate-500"
            :placeholder="$t('public.mailingListEmailPlaceholder')"
            :aria-invalid="!!fieldError"
            :aria-describedby="fieldError ? 'mailing-list-email-t4-error' : undefined"
            :disabled="submitting"
          >
            <div v-if="recaptchaEnabled && !recaptchaIsV3" class="w-full flex justify-center min-h-[78px]">
              <div ref="recaptchaContainerRef" aria-label="reCAPTCHA" />
            </div>
            <button
              type="submit"
              class="bg-[var(--rms-t4-primary,#ec5b13)] text-white px-8 py-4 rounded-xl font-bold hover:opacity-90 transition-opacity min-h-[44px] disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--rms-t4-primary,#ec5b13)] focus-visible:ring-offset-2"
              :disabled="submitting"
              data-testid="mailing-list-subscribe-btn"
            >
              {{ submitting ? $t('app.loading') : $t('public.mailingListSubscribe') }}
            </button>
          </form>
          <p id="mailing-list-email-t4-error" v-if="fieldError" class="text-red-600 dark:text-red-400 text-sm mt-2">{{ fieldError }}</p>
        </div>
      </div>
    </div>
    <!-- Template 5: ecommerce style, same layout as T4 with t5 primary -->
    <div v-else-if="template === 'template-5'" class="py-20 bg-[var(--rms-t5-primary,#2563eb)]/10">
      <div class="max-w-7xl mx-auto px-6">
        <div class="max-w-2xl mx-auto text-center">
          <h2 id="mailing-list-heading" class="rms-t5-heading mb-4">{{ $t('public.mailingListHeading') }}</h2>
          <p class="text-slate-600 dark:text-slate-400 mb-8">{{ $t('public.mailingListDescription') }}</p>
          <form
            novalidate
            class="flex flex-col sm:flex-row gap-3"
            :aria-busy="submitting"
            @submit.prevent="submit"
          >
            <p v-if="messageError" class="w-full text-red-600 dark:text-red-400 text-sm" role="alert">{{ messageError }}</p>
            <p v-if="messageSuccess" class="w-full text-green-600 dark:text-green-400 text-sm" role="status">{{ messageSuccess }}</p>
            <label for="mailing-list-email-t5" class="rms-sr-only">{{ $t('public.mailingListEmailLabel') }}</label>
            <input
              id="mailing-list-email-t5"
              v-model="email"
              type="email"
              data-testid="mailing-list-email"
              autocomplete="email"
              class="flex-grow px-6 py-4 rounded-xl border-none ring-1 ring-slate-200 dark:ring-slate-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-[var(--rms-t5-primary,#2563eb)] outline-none min-h-[44px] placeholder:text-slate-500"
              :placeholder="$t('public.mailingListEmailPlaceholder')"
              :aria-invalid="!!fieldError"
              :aria-describedby="fieldError ? 'mailing-list-email-t5-error' : undefined"
              :disabled="submitting"
            >
            <div v-if="recaptchaEnabled && !recaptchaIsV3" class="w-full flex justify-center min-h-[78px]">
              <div ref="recaptchaContainerRef" aria-label="reCAPTCHA" />
            </div>
            <button
              type="submit"
              class="bg-[var(--rms-t5-primary,#2563eb)] text-white px-8 py-4 rounded-xl font-bold hover:opacity-90 transition-opacity min-h-[44px] disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--rms-t5-primary,#2563eb)] focus-visible:ring-offset-2"
              :disabled="submitting"
              data-testid="mailing-list-subscribe-btn"
            >
              {{ submitting ? $t('app.loading') : $t('public.mailingListSubscribe') }}
            </button>
          </form>
          <p id="mailing-list-email-t5-error" v-if="fieldError" class="text-red-600 dark:text-red-400 text-sm mt-2">{{ fieldError }}</p>
        </div>
      </div>
    </div>
    <!-- Fallback (other templates): single-column -->
    <div v-else class="rms-mailing-list__inner">
      <h2 id="mailing-list-heading" class="rms-mailing-list__title">
        {{ $t('public.mailingListHeading') }}
      </h2>
      <p class="rms-mailing-list__description">
        {{ $t('public.mailingListDescription') }}
      </p>
      <form
        novalidate
        class="rms-mailing-list__form"
        :aria-busy="submitting"
        @submit.prevent="submit"
      >
        <p v-if="messageError" class="rms-mailing-list__error" role="alert">{{ messageError }}</p>
        <p v-if="messageSuccess" class="rms-mailing-list__success" role="status">{{ messageSuccess }}</p>
        <div class="rms-mailing-list__field">
          <label for="mailing-list-email" class="rms-mailing-list__label">
            {{ $t('public.mailingListEmailLabel') }}
          </label>
          <input
            id="mailing-list-email"
            v-model="email"
            type="email"
            data-testid="mailing-list-email"
            autocomplete="email"
            class="rms-mailing-list__input"
            :placeholder="$t('public.mailingListEmailPlaceholder')"
            :aria-invalid="!!fieldError"
            :aria-describedby="fieldError ? 'mailing-list-email-error' : undefined"
            :disabled="submitting"
          >
          <p id="mailing-list-email-error" v-if="fieldError" class="rms-mailing-list__field-error">{{ fieldError }}</p>
        </div>
        <button
          type="submit"
          class="rms-mailing-list__submit"
          :class="{ 'rms-mailing-list__submit--busy': submitting }"
          :disabled="submitting"
          data-testid="mailing-list-subscribe-btn"
        >
          {{ submitting ? $t('app.loading') : $t('public.mailingListSubscribe') }}
        </button>
      </form>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { mailingListService, normalizeApiError } from '@/services'
import { useRecaptcha } from '@/composables/useRecaptcha'

const props = defineProps({
  /** Restaurant object with slug and mailing_list_available (from PublicRestaurant.toJSON()). */
  restaurant: { type: Object, default: null },
  /** 'template-1' | 'template-2' | 'template-3' | 'template-4' | 'template-5' for section styling. */
  template: { type: String, default: 'template-1' },
})

const { t, locale } = useI18n()
const { recaptchaEnabled, recaptchaIsV3, loadScript, renderWidget, getToken: getRecaptchaToken, executeToken: executeRecaptchaToken, reset: resetRecaptcha } = useRecaptcha()
const recaptchaContainerRef = ref(null)

const email = ref('')
const submitting = ref(false)
const messageSuccess = ref('')
const messageError = ref('')
const fieldError = ref('')

const templateClass = computed(() => `rms-mailing-list--${props.template}`)

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function validate() {
  fieldError.value = ''
  messageError.value = ''
  const trimmed = (email.value || '').trim()
  if (!trimmed) {
    fieldError.value = t('public.mailingListEmailRequired')
    return false
  }
  if (!EMAIL_RE.test(trimmed)) {
    fieldError.value = t('public.mailingListEmailInvalid')
    return false
  }
  return true
}

async function submit() {
  messageSuccess.value = ''
  messageError.value = ''
  if (!validate()) return
  if (recaptchaEnabled.value && !recaptchaIsV3.value && !getRecaptchaToken()) {
    messageError.value = t('public.captchaRequired')
    return
  }
  const slug = props.restaurant?.slug
  if (!slug) return
  let captchaToken = ''
  if (recaptchaEnabled.value) {
    captchaToken = recaptchaIsV3.value ? await executeRecaptchaToken('mailing_list') : getRecaptchaToken()
    if (!captchaToken) {
      messageError.value = t('public.captchaRequired')
      return
    }
  }
  submitting.value = true
  try {
    const loc = locale?.value ?? locale ?? null
    const res = await mailingListService.subscribeMailingList(slug, email.value.trim(), loc, captchaToken || null)
    messageSuccess.value = res?.message ?? t('public.mailingListSuccess')
    email.value = ''
    if (recaptchaEnabled.value) resetRecaptcha()
  } catch (e) {
    const err = normalizeApiError(e)
    const msg = (err?.message && String(err.message).trim()) || ''
    messageError.value = msg || t('public.mailingListError')
    const validationErrs = e?.response?.data?.errors
    if (validationErrs?.captcha_token?.[0]) {
      messageError.value = validationErrs.captcha_token[0]
      if (recaptchaEnabled.value) resetRecaptcha()
    }
  } finally {
    submitting.value = false
  }
}

// When restaurant/template is set and reCAPTCHA enabled: load script and render v2 widget
watch(() => [props.restaurant?.slug, recaptchaEnabled.value, props.template], () => {
  if (!props.restaurant?.slug || !recaptchaEnabled.value || recaptchaIsV3.value) return
  loadScript().then(() => {
    nextTick(() => {
      const container = recaptchaContainerRef.value
      if (container) renderWidget(container).catch(() => {})
    })
  }).catch(() => {})
})
</script>

<style scoped>
.rms-mailing-list__inner {
  max-width: 36rem;
}
.rms-mailing-list__title {
  margin: 0 0 0.75rem 0;
  font-size: 1.875rem;
  font-weight: 700;
}
.rms-mailing-list__description {
  margin: 0 0 1.375rem 0;
  font-size: 0.9375rem;
  line-height: 1.5;
}
.rms-mailing-list__form {
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-start;
}
@media (min-width: 40rem) {
  .rms-mailing-list__form {
    flex-direction: row;
    gap: 0.75rem;
  }
}
.rms-mailing-list__field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  width: 100%;
}
@media (min-width: 40rem) {
  .rms-mailing-list__field {
    flex: 1 1 auto;
    min-width: 0;
    width: auto;
  }
}
.rms-mailing-list__label {
  font-size: 0.875rem;
  font-weight: 500;
}
.rms-mailing-list__input {
  min-height: 44px;
  padding: 0.5rem 0.75rem;
  border-width: 1px;
  border-radius: 0.5rem;
  font-size: 1rem;
}
.rms-mailing-list__input:focus-visible,
.rms-mailing-list__submit:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--rms-public-primary, currentColor);
}
.rms-mailing-list__submit {
  min-height: 44px;
  padding: 0.5rem 1rem;
  font-weight: 600;
  border-radius: 0.5rem;
  cursor: pointer;
  width: 100%;
  transition: filter 0.2s ease, background-color 0.2s ease;
}
@media (min-width: 40rem) {
  .rms-mailing-list__submit {
    flex: 0 0 auto;
    width: auto;
  }
}
@media (prefers-reduced-motion: reduce) {
  .rms-mailing-list__submit {
    transition: none;
  }
}
.rms-mailing-list__submit:hover:not(:disabled):not(.rms-mailing-list__submit--busy) {
  filter: brightness(0.95);
}
.rms-mailing-list__submit--busy {
  opacity: 0.85;
  pointer-events: none;
}
.rms-mailing-list__error,
.rms-mailing-list__success {
  margin: 0;
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  width: 100%;
}
@media (min-width: 40rem) {
  .rms-mailing-list__error,
  .rms-mailing-list__success {
    flex: 1 1 100%;
  }
}
.rms-mailing-list__field-error {
  margin: 0.375rem 0 0 0;
  font-size: 0.8125rem;
}

/* Template 1: warm neutral, primary CTA; one accent: light primary tint on section */
.rms-mailing-list--template-1 {
  border-top: 1px solid var(--t1-border, #dbdfe6);
}
.rms-mailing-list--template-1 .rms-mailing-list__inner {
  max-width: 72rem;
  margin-left: auto;
  margin-right: auto;
  padding: 3rem 1.5rem;
}
.rms-mailing-list--template-1 .rms-mailing-list__title,
.rms-mailing-list--template-1 .rms-mailing-list__description {
  color: var(--t1-text, #0f172a);
}
.rms-mailing-list--template-1 .rms-mailing-list__description {
  color: var(--t1-text-muted, #64748b);
}
.rms-mailing-list--template-1 .rms-mailing-list__label {
  color: var(--t1-text, #0f172a);
}
.rms-mailing-list--template-1 .rms-mailing-list__input {
  border-color: var(--t1-border, #e2e8f0);
  background: var(--t1-input-bg, #fff);
  color: var(--t1-text, #0f172a);
}
.rms-mailing-list--template-1 .rms-mailing-list__submit {
  background: var(--rms-public-primary, var(--t1-primary, #1152d4));
  color: white;
  border: none;
}
.rms-mailing-list--template-1 .rms-mailing-list__error {
  color: var(--t1-error, #dc2626);
  background: color-mix(in srgb, var(--t1-error, #dc2626) 12%, transparent);
  border-left: 3px solid var(--t1-error, #dc2626);
}
.rms-mailing-list--template-1 .rms-mailing-list__success {
  color: var(--t1-success, #16a34a);
  background: color-mix(in srgb, var(--t1-success, #16a34a) 12%, transparent);
  border-left: 3px solid var(--t1-success, #16a34a);
}
.rms-mailing-list--template-1 .rms-mailing-list__field-error {
  color: var(--t1-error, #dc2626);
  padding-left: 0.5rem;
  border-left: 3px solid var(--t1-error, #dc2626);
  background: color-mix(in srgb, var(--t1-error, #dc2626) 8%, transparent);
}

/* Template 2: utilitarian, border accent; two-column layout per reference code.html */
.rms-mailing-list--template-2 {
  background: #E5E7EB; /* concrete-gray */
  border-top: 2px solid #1E293B;
  border-bottom: 2px solid #1E293B;
}
.rms-mailing-list--template-2 .rms-mailing-list__inner {
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
  padding: 0;
  border: none;
}
.rms-mailing-list__inner--t2 {
  border: none !important;
  padding: 0 !important;
}
.rms-mailing-list__t2-grid {
  display: grid;
  grid-template-columns: 1fr;
  border-left: 1px solid #1E293B;
  border-right: 1px solid #1E293B;
}
@media (min-width: 768px) {
  .rms-mailing-list__t2-grid {
    grid-template-columns: 5fr 7fr; /* match reference: col-span-5 / col-span-7 */
  }
}
.rms-mailing-list__t2-copy {
  padding: 3rem 1.5rem;
  border-bottom: 1px solid #1E293B;
  background: #fff;
}
@media (min-width: 768px) {
  .rms-mailing-list__t2-copy {
    padding: 3rem 2.5rem;
    border-bottom: none;
    border-right: 1px solid #1E293B;
  }
}
.rms-mailing-list__t2-copy .rms-mailing-list__title--t2 {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
}
@media (min-width: 768px) {
  .rms-mailing-list__t2-copy .rms-mailing-list__title--t2 {
    font-size: 1.875rem;
  }
}
.rms-mailing-list__t2-copy .rms-mailing-list__description--t2 {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(30, 41, 59, 0.7);
  max-width: 20rem;
}
.rms-mailing-list__t2-form-wrap {
  position: relative;
  padding: 3rem 1.5rem;
}
@media (min-width: 768px) {
  .rms-mailing-list__t2-form-wrap {
    padding: 3rem 2.5rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
}
.rms-mailing-list__t2-form-bg {
  position: absolute;
  inset: 0;
  opacity: 0.1;
  pointer-events: none;
  background-image: repeating-linear-gradient(45deg, #1e293b 0, #1e293b 1px, transparent 0, transparent 50%);
  background-size: 10px 10px;
}
.rms-mailing-list__form--t2 {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: row;
  gap: 0.75rem;
}
.rms-mailing-list__t2-row {
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 2px solid #1E293B;
  border-radius: 0;
  box-shadow: 8px 8px 0 0 rgba(30, 41, 59, 1);
}
@media (min-width: 768px) {
  .rms-mailing-list__t2-row {
    flex-direction: row;
  }
}
.rms-mailing-list__t2-input-wrap {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  background: #fff;
  padding: 0 1.5rem;
  border-bottom: 2px solid #1E293B;
}
@media (min-width: 768px) {
  .rms-mailing-list__t2-input-wrap {
    border-bottom: none;
    border-right: 2px solid #1E293B;
  }
}
.rms-mailing-list__input--t2 {
  flex: 1;
  min-height: 3.25rem;
  padding: 1rem 0;
  border: none;
  background: transparent;
  border-radius: 0;
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 0.875rem;
  font-weight: 700;
  color: #1E293B;
  box-shadow: none !important;
}
.rms-mailing-list__input--t2::placeholder {
  color: rgba(30, 41, 59, 0.3);
}
.rms-mailing-list__input--t2:focus {
  outline: none;
}
.rms-mailing-list__input--t2:focus-visible {
  outline: 2px solid #B35C38;
  outline-offset: 2px;
}
.rms-mailing-list__submit--t2:focus-visible {
  outline: 2px solid #1E293B;
  outline-offset: 2px;
}
.rms-mailing-list__submit--t2 {
  min-height: 3.25rem;
  padding: 1.25rem 2.5rem;
  background: #B35C38 !important; /* oxidized-copper */
  color: #fff !important;
  border: none !important;
  border-radius: 0;
  font-family: var(--font-industrial, 'Barlow Condensed', sans-serif);
  font-weight: 800;
  font-size: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  flex-shrink: 0;
  transition: background-color 0.2s ease;
}
.rms-mailing-list__submit--t2:hover:not(:disabled):not(.rms-mailing-list__submit--busy) {
  background: #1E293B !important; /* charcoal-blue */
  filter: none;
}
.rms-mailing-list--template-2 .rms-mailing-list__title {
  font-family: var(--font-body, inherit);
  font-size: 1.875rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--t2-text, #1E293B);
  margin-bottom: 0.75rem;
}
.rms-mailing-list--template-2 .rms-mailing-list__description {
  color: var(--t2-text, #1E293B);
  opacity: 0.85;
}
.rms-mailing-list--template-2 .rms-mailing-list__label {
  color: var(--t2-text, #1E293B);
}
.rms-mailing-list--template-2 .rms-mailing-list__input {
  border-color: var(--t2-border, #1E293B);
  background: var(--t2-bg-elevated, #fff);
  color: var(--t2-text, #1E293B);
}
.rms-mailing-list--template-2 .rms-mailing-list__submit {
  background: var(--rms-public-primary, var(--t2-primary, #1152d4));
  color: white;
  border: 2px solid var(--t2-border, #1E293B);
}
.rms-mailing-list--template-2 .rms-mailing-list__submit:hover:not(:disabled):not(.rms-mailing-list__submit--busy) {
  filter: brightness(0.95);
}
.rms-mailing-list--template-2 .rms-mailing-list__error,
.rms-mailing-list--template-2 .rms-mailing-list__success {
  order: -1;
  width: 100%;
  margin: 0 0 0.5rem 0;
  padding: 0.75rem 1rem;
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 600;
  background: #fff;
  border: 2px solid #1E293B;
  box-shadow: 6px 6px 0 0 rgba(30, 41, 59, 1);
}
.rms-mailing-list--template-2 .rms-mailing-list__error {
  color: var(--t2-error, #dc2626);
  border-left: 4px solid var(--t2-error, #dc2626);
}
.rms-mailing-list--template-2 .rms-mailing-list__success {
  color: var(--t2-success, #16a34a);
  border-left: 4px solid var(--t2-success, #16a34a);
}
.rms-mailing-list--template-2 .rms-mailing-list__field-error {
  color: var(--t2-error, #dc2626);
  padding-left: 0.5rem;
  border-left: 3px solid var(--t2-error, #dc2626);
  background: color-mix(in srgb, var(--t2-error, #dc2626) 8%, transparent);
}

/* Template 1: centered section — no section background or borders (blends with page) */
.rms-mailing-list--template-1 .rms-mailing-list__t1-input {
  border-color: var(--t1-border, #dbdfe6);
}
.rms-mailing-list--template-1 .rms-mailing-list__t1-input::placeholder {
  color: var(--t1-neutral-muted, #616f89);
}
.rms-mailing-list--template-1 .rms-mailing-list__t1-input:focus-visible {
  border-color: var(--t1-primary, #1152d4);
  box-shadow: 0 0 0 2px var(--t1-primary, #1152d4);
}
.rms-mailing-list--template-1 .rms-mailing-list__t1-submit {
  background: var(--rms-public-primary, var(--t1-primary, #1152d4));
  color: #fff;
  border: none;
}
.rms-mailing-list--template-1 .rms-mailing-list__t1-submit:focus-visible {
  box-shadow: 0 0 0 2px var(--t1-primary, #1152d4);
}
@media (prefers-reduced-motion: reduce) {
  .rms-mailing-list--template-1 .rms-mailing-list__t1-submit {
    transition: none;
  }
}
</style>
